import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subject, merge } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ITableKey } from '../basic-table/basic-table.constants';
import { LoggerService } from 'src/app/services/logger.service';
import { IPaginationEvent, ITableButtonEvent, TABLE_CONSTANTS } from './be-paginated-table.constants';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { IObjectKey } from '../../models/objects';
import { IDashboardOperation } from '../../models/operation.model';

export function CustomPaginator() {
  const customPaginatorIntl = new MatPaginatorIntl();
  customPaginatorIntl.itemsPerPageLabel = 'Dimensione pagina';
  return customPaginatorIntl;
}

@Component({
  selector: 'app-be-paginated-table',
  templateUrl: './be-paginated-table.component.html',
  styleUrls: ['./be-paginated-table.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useValue: CustomPaginator() }
  ]
})
export class BePaginatedTableComponent implements AfterViewInit, OnDestroy {

  @Input('tableKeys')
  set _tableKeys(val: ITableKey[]) {
    if (val) {
      this.tableKeys = val;
      this.displayedColumns = this.tableKeys.map(x => x.key);
    }
  }

  @Input() dataSource: any;
  @Input() matSortActive: string = '';
  @Input() matSortDirection: 'asc' | 'desc' = 'asc';

  @Output() changeRequested: EventEmitter<IPaginationEvent> = new EventEmitter<IPaginationEvent>();
  @Output() itemSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() actionSelected: EventEmitter<ITableButtonEvent<any>> = new EventEmitter<ITableButtonEvent<any>>();
  @Output() rowsSelected: EventEmitter<Set<string>> = new EventEmitter<Set<string>>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  tableKeys: ITableKey[] = [];
  displayedColumns = [];
  tableConstants = TABLE_CONSTANTS;
  selectedSet = new Set<string>();
  destroy$ = new Subject<void>();

  radioSelectionGroup: any = null;

  ngAfterViewInit() {
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.paginator.pageIndex = 0);
    merge(
      this.sort.sortChange,
      this.paginator.page
    )
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const obj: IPaginationEvent = {
            sortField: this.sort.active,
            sortDirection: this.sort.direction,
            pageIndex: this.paginator.pageIndex,
            pageSize: this.paginator.pageSize
          }
          this.changeRequested.emit(obj)
        }
        ))
      .subscribe();
      this.dataSource?.refreshPaginator$?.pipe(takeUntil(this.destroy$))
      .subscribe(refresh => {
        if (refresh) {
          this.paginator.pageIndex = 0;
        }
      })
  }

  onButtonClick(key: string, element: any) {
    LoggerService.logInfo(this.constructor.name, 'onButtonClick', { key: key, element: element });
    this.actionSelected.emit({
      key: key,
      detailObject: element
    })
  }

  selectItem(element: any) {
    LoggerService.logInfo(this.constructor.name, 'selectItem', { element });
    this.radioSelectionGroup = element.id;
    this.itemSelected.emit(this.radioSelectionGroup);
  }

  getWidth(width: string) {
    if (width !== null && width !== undefined) {
      return { 'width': width, 'flex': 'unset' };
    }
  }

  selectAllCheckboxes() {
    LoggerService.logInfo(this.constructor.name, 'selectAllCheckboxes', { selected: this.allCheckboxesSelected });
    if (this.allCheckboxesSelected) {
      this.selectedSet.clear();
    } else {
      const list: any[] = this.dataSource?.currentPage;
      if (list && list.length > 0) {
        list.forEach(item => this.selectedSet.add(item.id));
      }
    }
    this.rowsSelected.emit(this.selectedSet);
  }

  get allCheckboxesSelected(): boolean {
    const list: any[] = this.dataSource?.currentPage;
    if (list && list.length > 0) {
      const check = list.some(x => !this.selectedSet.has(x.id));
      return !check;
    }
    return false;
  }

  onCheckboxChange(element: any) {
    LoggerService.logInfo(this.constructor.name, 'onCheckboxChange', { element });
    if (this.selectedSet.has(element.id)) {
      this.selectedSet.delete(element.id);
    } else {
      this.selectedSet.add(element.id);
    }
    this.rowsSelected.emit(this.selectedSet);
  }

  checkboxIsSelected(element): boolean {
    return this.selectedSet.has(element.id);
  }

  onRowClick(row: any) {
    LoggerService.logInfo(this.constructor.name, 'onRowClick', { row });
    this.selectItem(row);
  }

  getCompanyImage(item: IDashboardOperation): string {
    if (item && item.companyId) {
      return CommonUtils.getCompanyImage(item.companyId);
    }
    return '';
  }

  getStatusLabel(val: IDashboardOperation): string {
    return CommonUtils.getStatusLabel(val);
  }

  getStatusBadge(val: IDashboardOperation): string {
    return CommonUtils.getStatusBadge(val);
  }

  getValueByPath(detail: any, item: IObjectKey): string {
    const value = CommonUtils.getValueByPath(detail, item);
    return value;
  }

  ngOnDestroy(): void {
    LoggerService.logInfo(this.constructor.name, 'ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

}
